/**
 * Single source of truth for one easement in the review session.
 * UI never patches this object; only the reducer writes it.
 */
import type { LLMAnalysisResultItem } from "src/modules/ANNModule/ANNLLMAnalyzer";
import type { ServitutAiFieldStatus } from "src/modules/ANNModule/ANNServitutProvenance";
import type { NormalizedServitutIdentity } from "src/modules/ANNModule/ANNServitutIdentity";
import type { ServitutAnalysisFieldKey, ServitutFieldOverrideMap } from "src/modules/ANNModule/ANNServitutFieldAnnotations";
import { emptyPageModel, type PageModel } from "./pages";

/** Tingbog electronic package; kept loose so the store does not import UI modules. */
export type ServitutElectronicPackageServitut = Record<string, any>;

export type PreparedAktSource = "korrektAkt" | "servitutAkt" | "textAkt" | "ovrigAkt" | "customAkt";
export type ServitutRowHandling = "legacyLlmPdf" | "electronicZipPackage";
export type ServitutPdfStatus = "pending" | "ready" | "missing" | "failed";
export type ServitutRowJob = "idle" | "locating" | "analyzing" | "saving" | "fetching";
export type ServitutReviewOrigin = "ai" | "person";
/** How the current bytes were obtained. Survives Ja (upload → annotation). */
export type DocumentFileOrigin = "upload" | "akt" | "digital";

export type DocumentBinding =
   | { kind: "none" }
   | { kind: "tingbog_akt"; aktId: string; url?: string; source: PreparedAktSource; filename?: string }
   | { kind: "tingbog_digital"; electronicServitut: ServitutElectronicPackageServitut }
   | { kind: "upload"; s3Key: string; filename: string }
   | {
      kind: "annotation";
      recordId: string;
      s3Key: string;
      extractedAktPages: number[];
      filename?: string;
      s3Url?: string;
      mimeType?: string;
      size?: number;
      origin?: DocumentFileOrigin;
   }
   | { kind: "rigsarkivet" };

export type RegistryBinding = Exclude<DocumentBinding, { kind: "none" | "annotation" | "rigsarkivet" }>;

export function isLandRegistryBinding(document: DocumentBinding): document is Extract<DocumentBinding, { kind: "tingbog_akt" | "tingbog_digital" }> {
   return document.kind === "tingbog_akt" || document.kind === "tingbog_digital";
}

/** Tingbog fallback for Fortryd. An upload must not erase a fetched akt. */
export function registryBindingFor(
   document: DocumentBinding,
   previous?: RegistryBinding,
): RegistryBinding | undefined {
   if (isLandRegistryBinding(document)) {
      return document;
   }
   if (document.kind === "upload") {
      return previous && isLandRegistryBinding(previous) ? previous : document;
   }
   return previous;
}

export type FieldSlot = {
   aiStatus: ServitutAiFieldStatus;
   aiText: string;
   acceptedNoteId?: string;
   acceptedText?: string;
};

export type ServitutLandRegistry = {
   servitut: any;
   handling: ServitutRowHandling;
   includedBfes: number[];
   displayAktId: string;
   priority: string;
   summary: string;
   exportFileName?: string;
};

export type ServitutReview = {
   state: "unchecked" | "checked" | "rejected";
   origin?: ServitutReviewOrigin;
};

export type ServitutRow = {
   id: string;
   identity: NormalizedServitutIdentity;
   landRegistry: ServitutLandRegistry;
   document: DocumentBinding;
   /** Last land-registry (or upload-if-no-akt) binding, used by Fortryd. */
   registryBinding?: RegistryBinding;
   pdfStatus: ServitutPdfStatus;
   pages: PageModel;
   review: ServitutReview;
   analysis?: { item: LLMAnalysisResultItem; stale: boolean };
   fields: Record<ServitutAnalysisFieldKey, FieldSlot>;
   /** Live AI/edit differs from the saved field annotation; Reset restores the annotation. */
   fieldOverridePending?: ServitutFieldOverrideMap;
   fieldOverrideStamp?: number;
   stedfaestelseId?: string;
   job: ServitutRowJob;
   sourceMessage?: string;
};

export function emptyFieldSlot(): FieldSlot {
   return { aiStatus: "not_run", aiText: "" };
}

export function emptyFields(): Record<ServitutAnalysisFieldKey, FieldSlot> {
   return {
      resume: emptyFieldSlot(),
      entitledParty: emptyFieldSlot(),
      condition: emptyFieldSlot(),
      statement: emptyFieldSlot(),
   };
}

export function createSkeletonRow(partial: {
   id: string;
   identity: NormalizedServitutIdentity;
   landRegistry: ServitutLandRegistry;
   document?: DocumentBinding;
   pdfStatus?: ServitutPdfStatus;
   sourceMessage?: string;
}): ServitutRow {
   const document = partial.document || { kind: "none" };
   return {
      id: partial.id,
      identity: partial.identity,
      landRegistry: partial.landRegistry,
      document,
      registryBinding: registryBindingFor(document),
      pdfStatus: partial.pdfStatus || "pending",
      pages: emptyPageModel(),
      review: { state: "unchecked" },
      fields: emptyFields(),
      job: "idle",
      sourceMessage: partial.sourceMessage,
   };
}

export function isDigitalRow(row: ServitutRow): boolean {
   return row.landRegistry.handling === "electronicZipPackage";
}

/** User-supplied PDF. Digital packages and akt PDFs are not this. */
export function isUserFileDocument(row: ServitutRow): boolean {
   return row.document.kind === "upload"
      || (row.document.kind === "annotation" && row.document.origin === "upload");
}

export function electronicPackageForRow(row: ServitutRow): ServitutElectronicPackageServitut | undefined {
   if (row.document.kind === "tingbog_digital") {
      return row.document.electronicServitut;
   }
   if (row.registryBinding?.kind === "tingbog_digital") {
      return row.registryBinding.electronicServitut;
   }
   return undefined;
}

export function digitalOpenBinding(row: ServitutRow): Extract<DocumentBinding, { kind: "tingbog_digital" }> | undefined {
   if (row.document.kind === "tingbog_digital") {
      return row.document;
   }
   if (row.registryBinding?.kind === "tingbog_digital") {
      return row.registryBinding;
   }
   return undefined;
}

export function isFetched(row: ServitutRow): boolean {
   if (row.pdfStatus === "ready") {
      return true;
   }
   if (row.document.kind === "annotation") {
      return true;
   }
   if (row.document.kind === "upload") {
      return Boolean(row.document.s3Key);
   }
   if (row.document.kind === "tingbog_digital") {
      return true;
   }
   return false;
}

export function hasAcceptedField(row: ServitutRow, field: ServitutAnalysisFieldKey): boolean {
   return Boolean(row.fields[field].acceptedNoteId && (row.fields[field].acceptedText || "").trim());
}

export function needsLandRegistryRebind(row: ServitutRow): boolean {
   if (row.registryBinding) {
      return false;
   }
   if (row.document.kind === "upload" || row.document.kind === "tingbog_akt" || row.document.kind === "tingbog_digital") {
      return false;
   }
   return true;
}

/** Saved annotation PDF is enough to open the row; Tingbog AKT wait until Fortryd. */
export function needsDeferredAktFetch(row: ServitutRow): boolean {
   if (row.review.state === "rejected") {
      return false;
   }
   if (row.pdfStatus === "missing" || row.pdfStatus === "failed") {
      return false;
   }
   if (
      row.document.kind === "annotation"
      || row.document.kind === "upload"
      || row.document.kind === "tingbog_akt"
      || row.document.kind === "tingbog_digital"
      || row.document.kind === "rigsarkivet"
   ) {
      return false;
   }
   return row.document.kind === "none";
}

/** First paint should already show Henter akt… for every row that still needs a Tingbog PDF. */
export function withDeferredAktFetchJob(row: ServitutRow): ServitutRow {
   if (needsDeferredAktFetch(row) && row.job === "idle") {
      return { ...row, job: "fetching" };
   }
   return row;
}
